// File: src/services/communicationService.ts
// ==============================================================================
// VERSION INFORMATION
// ==============================================================================
// __version__ = "1.1.2"
//
// Changelog:
//
// Version 1.1.2:
// - [Added] `updateAvailabilityException` function to call the new `PUT`
//   endpoint for editing an existing slot override.
//
// Version 1.1.1:
// - [Added] `getAvailabilityExceptions` function to fetch all of a coach's
//   slot overrides, resolving a build error in the availability page.
//
// Version 1.1.0:
// - [Added] New functions `createAvailabilityException` and
//   `deleteAvailabilityException` to support granular, single-instance slot
//   management for coaches.
//
// Version 1.0.0:
// - Initial version of the communication service.
// ==============================================================================
import apiClient from './api';
import {
  Message, Appointment, Notification, MsgResponse,
  CoachAvailabilitySlot, CoachAvailabilityCreatePayload, CoachAvailabilityUpdatePayload,
  AvailableTimeBlock, AppointmentRequestCreatePayload, AppointmentProposalCreatePayload,
  AppointmentActionPayload,
  AppointmentCreatePayload, AppointmentUpdatePayload,
  GetAppointmentsParams,
  AvailabilityException, AvailabilityExceptionCreatePayload, AvailabilityExceptionUpdatePayload
} from '../types';
import { AppointmentStatusEnum } from '../types/enums';


// For sending messages (multipart/form-data)
const sendMessage = async (formData: FormData): Promise<Message> => {
  const { data } = await apiClient.post<Message>('/communication/messages/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
};

const getConversation = async (otherUserId: number, skip = 0, limit = 50): Promise<Message[]> => {
  const { data } = await apiClient.get<Message[]>(`/communication/messages/conversation/${otherUserId}`, {
    params: { skip, limit },
  });
  return data;
};

// NEW: Payload for updating a message
export interface MessageUpdatePayload {
  content: string;
}

// NEW: Function to update a message
const updateMessage = async (messageId: number, payload: MessageUpdatePayload): Promise<Message> => {
  const { data } = await apiClient.put<Message>(`/communication/messages/${messageId}`, payload);
  return data;
};

// NEW: Function to delete a single message
const deleteMessage = async (messageId: number): Promise<void> => {
  await apiClient.delete(`/communication/messages/${messageId}`);
};

// NEW: Function to delete an entire conversation (if implemented on backend)
const deleteConversation = async (otherUserId: number): Promise<MsgResponse> => {
    const { data } = await apiClient.delete<MsgResponse>(`/communication/messages/conversation/${otherUserId}`);
    return data;
};

// NEW: Function to get a single message by ID
const getMessageById = async (messageId: number): Promise<Message> => {
  const { data } = await apiClient.get<Message>(`/communication/messages/${messageId}`);
  return data;
};

const markMessageAsRead = async (messageId: number): Promise<Message> => {
  const { data } = await apiClient.post<Message>(`/communication/messages/${messageId}/read`);
  return data;
};

// --- NEW: GROUP MESSAGING ---
const getGroupConversation = async (groupId: number, skip = 0, limit = 50): Promise<Message[]> => {
    const { data } = await apiClient.get<Message[]>(`/communication/groups/${groupId}/messages`, {
        params: { skip, limit },
    });
    return data;
};

const sendGroupMessage = async (groupId: number, formData: FormData): Promise<Message> => {
    const { data } = await apiClient.post<Message>(`/communication/groups/${groupId}/messages`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
};


// --- APPOINTMENTS (General) ---
const createAppointment = async (payload: AppointmentCreatePayload): Promise<Appointment> => {
  const { data } = await apiClient.post<Appointment>('/communication/appointments/', payload);
  return data;
};

const getMyAppointments = async (params: GetAppointmentsParams = {}): Promise<Appointment[]> => {
  const { data } = await apiClient.get<Appointment[]>('/communication/appointments/me', { params });
  return data;
};

const updateAppointment = async (appointmentId: number, payload: AppointmentUpdatePayload): Promise<Appointment> => {
  const { data } = await apiClient.put<Appointment>(`/communication/appointments/${appointmentId}`, payload);
  return data;
};

const deleteAppointment = async (appointmentId: number): Promise<MsgResponse> => {
  const { data } = await apiClient.delete<MsgResponse>(`/communication/appointments/${appointmentId}`);
  return data;
};


// --- APPOINTMENT REQUESTS (Client side) ---
const requestAppointment = async (payload: AppointmentRequestCreatePayload): Promise<Appointment> => {
  const { data } = await apiClient.post<Appointment>('/clients/me/appointment-requests', payload);
  return data;
};

const cancelAppointmentRequest = async (appointmentId: number, reason?: string | null): Promise<Appointment> => {
  const payload = { cancellation_reason: reason || null };
  const { data } = await apiClient.patch<Appointment>(`/clients/me/appointment-requests/${appointmentId}/cancel`, payload);
  return data;
};

const getClientAvailableSlots = async (start_date: string, end_date: string): Promise<AvailableTimeBlock[]> => {
  const { data } = await apiClient.get<AvailableTimeBlock[]>('/clients/me/coach-available-slots', {
    params: { start_date, end_date }
  });
  return data;
};

const getClientAppointmentProposals = async (status?: AppointmentStatusEnum[]): Promise<Appointment[]> => {
  const params: { status?: AppointmentStatusEnum[] } = {};
  if (status) {
    params.status = status;
  }
  const { data } = await apiClient.get<Appointment[]>('/clients/me/appointment-proposals', { params });
  return data;
};

const acceptAppointmentProposal = async (appointmentId: number): Promise<Appointment> => {
  const { data } = await apiClient.patch<Appointment>(`/clients/me/appointment-proposals/${appointmentId}/accept`);
  return data;
};

const rejectAppointmentProposal = async (appointmentId: number, payload: AppointmentActionPayload): Promise<Appointment> => {
  const { data } = await apiClient.patch<Appointment>(`/clients/me/appointment-proposals/${appointmentId}/reject`, payload);
  return data;
};


// --- APPOINTMENT REQUESTS (Coach side) ---
const getCoachAppointmentRequests = async (status?: AppointmentStatusEnum[]): Promise<Appointment[]> => {
  const params: { status?: AppointmentStatusEnum[] } = {};
  if (status) {
    params.status = status;
  }
  const { data } = await apiClient.get<Appointment[]>('/coaches/me/appointment-requests', { params });
  return data;
};

const approveAppointmentRequest = async (appointmentId: number, payload?: AppointmentActionPayload): Promise<Appointment> => {
  const { data } = await apiClient.patch<Appointment>(`/coaches/me/appointment-requests/${appointmentId}/approve`, payload);
  return data;
};

const rejectAppointmentRequest = async (appointmentId: number, payload: AppointmentActionPayload): Promise<Appointment> => {
  const { data } = await apiClient.patch<Appointment>(`/coaches/me/appointment-requests/${appointmentId}/reject`, payload);
  return data;
};

const createCoachAppointmentProposal = async (payload: AppointmentProposalCreatePayload): Promise<Appointment> => {
  const { data } = await apiClient.post<Appointment>('/coaches/me/appointment-proposals', payload);
  return data;
};


// --- COACH AVAILABILITY SLOTS ---
const createCoachAvailabilitySlot = async (payload: CoachAvailabilityCreatePayload): Promise<CoachAvailabilitySlot> => {
  const { data } = await apiClient.post<CoachAvailabilitySlot>('/coaches/me/availability', payload);
  return data;
};

const getCoachAvailabilitySlots = async (params: { start_date?: string; end_date?: string; is_active?: boolean } = {}): Promise<CoachAvailabilitySlot[]> => {
  const { data } = await apiClient.get<CoachAvailabilitySlot[]>('/coaches/me/availability', { params });
  return data;
};

const updateCoachAvailabilitySlot = async (slotId: number, payload: CoachAvailabilityUpdatePayload): Promise<CoachAvailabilitySlot> => {
  const { data } = await apiClient.put<CoachAvailabilitySlot>(`/coaches/me/availability/${slotId}`, payload);
  return data;
};

const deleteCoachAvailabilitySlot = async (slotId: number): Promise<MsgResponse> => {
  const { data } = await apiClient.delete<MsgResponse>(`/coaches/me/availability/${slotId}`);
  return data;
};

// --- NEW: AVAILABILITY EXCEPTIONS ---
const getAvailabilityExceptions = async (): Promise<AvailabilityException[]> => {
  const { data } = await apiClient.get<AvailabilityException[]>('/coaches/me/availability/exceptions');
  return data;
};

const createAvailabilityException = async (payload: AvailabilityExceptionCreatePayload): Promise<AvailabilityException> => {
  const { data } = await apiClient.post<AvailabilityException>('/coaches/me/availability/exceptions', payload);
  return data;
};

const updateAvailabilityException = async (exceptionId: number, payload: AvailabilityExceptionUpdatePayload): Promise<AvailabilityException> => {
  const { data } = await apiClient.put<AvailabilityException>(`/coaches/me/availability/exceptions/${exceptionId}`, payload);
  return data;
};

const deleteAvailabilityException = async (exceptionId: number): Promise<MsgResponse> => {
  const { data } = await apiClient.delete<MsgResponse>(`/coaches/me/availability/exceptions/${exceptionId}`);
  return data;
};
// --- END NEW ---


// --- NOTIFICATIONS ---
const getMyNotifications = async (unreadOnly = false, limit = 50): Promise<Notification[]> => {
  const { data } = await apiClient.get<Notification[]>('/communication/notifications/me', {
    params: { unread_only: unreadOnly, limit },
  });
  return data;
};

const markNotificationAsRead = async (notificationId: number): Promise<Notification> => {
  const { data } = await apiClient.post<Notification>(`/communication/notifications/${notificationId}/read`);
  return data;
};

const markAllNotificationsAsRead = async (): Promise<MsgResponse> => {
  const { data } = await apiClient.post<MsgResponse>('/communication/notifications/me/read-all');
  return data;
};

const clearAllNotifications = async (): Promise<MsgResponse> => {
    const { data } = await apiClient.delete<MsgResponse>('/communication/notifications/me/clear-all');
    return data;
};

const getUnreadNotificationCount = async (): Promise<{ unread_count: number }> => {
    try {
        const { data } = await apiClient.get<{ unread_count: number }>('/communication/notifications/me/unread_count');
        return data;
    } catch (error) {
        console.warn("Unread count endpoint might not exist, falling back to fetching list.");
        const notifications = await getMyNotifications(true, 1);
        return { unread_count: notifications.length };
    }
};


export default {
  sendMessage,
  getConversation,
  markMessageAsRead,
  updateMessage,
  deleteMessage,
  deleteConversation,
  getMessageById,
  getGroupConversation,
  sendGroupMessage,
  createAppointment,
  getMyAppointments,
  updateAppointment,
  deleteAppointment,
  requestAppointment,
  cancelAppointmentRequest,
  getClientAvailableSlots,
  getClientAppointmentProposals,
  acceptAppointmentProposal,
  rejectAppointmentProposal,
  getCoachAppointmentRequests,
  approveAppointmentRequest,
  rejectAppointmentRequest,
  createCoachAppointmentProposal,
  createCoachAvailabilitySlot,
  getCoachAvailabilitySlots,
  updateCoachAvailabilitySlot,
  deleteCoachAvailabilitySlot,
  getAvailabilityExceptions,
  createAvailabilityException,
  updateAvailabilityException,
  deleteAvailabilityException,
  getMyNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  clearAllNotifications,
  getUnreadNotificationCount,
};